import { useDepartmentService } from '@/api/services/departmentService';
import { useOrg } from '@/organization/useOrg';
import {
  getCurrentOrganizationUser,
  updateOrganizationUser,
} from '@/services/userService';
import type { Department } from '@/types/department';
import type { Member } from '@/types/user';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

// Import profile icons
import pencilIcon from '@/assets/icons/pencil-icon.png';
import profileIcon from '@/assets/icons/profile-icon.png';

// Import components and constants
import {
  AddTagButton,
  COLORS,
  FieldDisplay,
  FieldEdit,
  inputStyles,
  LABEL_SIZE,
  PermissionModal,
  SECTION_HEADER_WIDTH,
  SectionHeader,
  TAG_BUTTON_WIDTH,
  TAG_FONT,
  TAG_HEIGHT,
  TAG_INPUT_HEIGHT,
  VALUE_SIZE,
} from './ProfilePageComponents';

// =============================================================================
// RESPONSIVE SIZING SYSTEM
// =============================================================================
// All values use vw (viewport width) for horizontal elements
// and vh (viewport height) for vertical elements
// Reference: 1806x768 at 1920x1080 screen
// Width: 1806/1920 = 94.06vw, Height: 768/1080 = 71.1vh
// =============================================================================

// Main container - scales with viewport to fit without scrolling
const CONTAINER_WIDTH = 'clamp(735px, 92vw, 1766px)';
const CONTAINER_MIN_WIDTH = '850px';
const CONTAINER_HEIGHT =
  'clamp(350px, calc(60vh + max(0px, (100vh - 800px) * 0.4)), 850px)';
const CONTAINER_PADDING_X = 'clamp(16px, 1.5vw, 24px)';
const CONTAINER_PADDING_Y = 'clamp(12px, 1.2vh, 18px)';
const CONTAINER_MARGIN_X = 'clamp(24px, 3vw, 60px)';

// Profile section on left side - scales down more on small screens
const PROFILE_SECTION_WIDTH = 'clamp(130px, 10.4vw, 220px)';

// Avatar sizing - scales down more on small screens
const AVATAR_SIZE = 'clamp(100px, 8.5vw, 163px)';

// Edit Profile button sizing - scales down more on small screens
const EDIT_BUTTON_WIDTH = 'clamp(120px, 8.5vw, 168px)';
const EDIT_BUTTON_HEIGHT = 'clamp(28px, 4.5vh, 46px)';
const EDIT_BUTTON_FONT = 'clamp(13px, 1.1vw, 18px)';
const EDIT_BUTTON_ICON = 'clamp(10px, 0.9vw, 18px)';

// Other button sizing
const NAV_BUTTON_HEIGHT = 'clamp(26px, 3.5vh, 40px)';
const NAV_BUTTON_FONT = 'clamp(10px, 1.0vw, 15px)';
const SAVE_BUTTON_WIDTH = 'clamp(110px, 8.5vw, 140px)';

// Spacing - reduced for better vertical fit
const SECTION_GAP = 'clamp(16px, 3vw, 80px)'; // Gap between Personal Info and Contact Info - scales with screen size
const FIELD_GAP = 'clamp(4px, 0.8vh, 12px)'; // Reduced for vertical compactness
const PROFILE_TO_INFO_GAP = 'clamp(6px, 1vw, 16px)'; // Gap between profile section and info sections

export function ProfilePage() {
  const { selectedOrg } = useOrg();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  // Edit form state
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPronouns, setEditPronouns] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editDepartmentIds, setEditDepartmentIds] = useState<string[]>([]);

  // Dropdown states for role and department
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);

  // Permission modal state
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [permissionModalMessage, setPermissionModalMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<{
    type: 'role' | 'department';
    action: 'add' | 'remove';
    value: string;
  } | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success',
  );

  const organizationId: string = selectedOrg?.id || '';

  const userQuery = useQuery({
    queryKey: ['organization-current-user', organizationId],
    queryFn: () => getCurrentOrganizationUser(organizationId),
    enabled: !!organizationId,
  });

  // Fetch available departments
  const departmentService = useDepartmentService();
  const departmentsQuery = useQuery({
    queryKey: ['departments', organizationId],
    queryFn: async () => {
      const response = await departmentService.list();
      return response.data; // Extract data from ApiResponse
    },
    enabled: !!selectedOrg && isEditing,
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      first_name: string;
      last_name: string;
      phone?: string;
      pronouns?: string;
      departmentIds?: string[];
      role?: string;
    }) =>
      updateOrganizationUser(
        organizationId,
        member?.userDetails.uuid || '',
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-current-user', organizationId],
      });
      setIsEditing(false);
      setSnackbarMessage('Profile updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error: any) => {
      setSnackbarMessage(error?.message || 'Failed to update profile');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const member: Member | null = userQuery.data ? userQuery.data : null;
  const availableDepartments: Department[] = departmentsQuery.data || [];

  // Available role options - must match backend ROLE_CHOICES
  const roleOptions = [
    'Organization Admin',
    'Program Manager',
    'Budget Manager',
    'General User',
  ];

  // Initialize edit form when entering edit mode
  const handleStartEdit = () => {
    if (member) {
      setEditFirstName(member.userDetails.firstName || '');
      setEditLastName(member.userDetails.lastName || '');
      setEditPronouns(member.userDetails.pronouns || '');
      setEditPhone(member.userDetails.phone || '');
      setEditRole(member.role || 'General User');
      setEditDepartmentIds(
        member.userDetails.departments?.map((d) => d.id) || [],
      );
    }
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    updateMutation.mutate({
      first_name: editFirstName,
      last_name: editLastName,
      phone: editPhone,
      pronouns: editPronouns,
      role: editRole,
      departmentIds: editDepartmentIds,
    });
  };

  const handleSelectRole = (role: string) => {
    setPermissionModalMessage('ASK PERMISSION TO CHANGE/ADD THIS ROLE?');
    setPendingAction({ type: 'role', action: 'add', value: role });
    setPermissionModalOpen(true);
    setShowRoleDropdown(false);
  };

  const handleRemoveRole = () => {
    setPermissionModalMessage('ASK PERMISSION TO REMOVE THIS ROLE?');
    setPendingAction({ type: 'role', action: 'remove', value: '' });
    setPermissionModalOpen(true);
  };

  const handleSelectDepartment = (deptId: string) => {
    setPermissionModalMessage('ASK PERMISSION TO ADD THIS DEPARTMENT?');
    setPendingAction({ type: 'department', action: 'add', value: deptId });
    setPermissionModalOpen(true);
    setShowDepartmentDropdown(false);
  };

  const handleRemoveDepartment = (deptId: string) => {
    setPermissionModalMessage('ASK PERMISSION TO REMOVE THIS DEPARTMENT?');
    setPendingAction({ type: 'department', action: 'remove', value: deptId });
    setPermissionModalOpen(true);
  };

  const handlePermissionConfirm = () => {
    if (pendingAction) {
      if (pendingAction.type === 'role') {
        if (pendingAction.action === 'add') {
          setEditRole(pendingAction.value);
        } else if (pendingAction.action === 'remove') {
          setEditRole('');
        }
      } else if (pendingAction.type === 'department') {
        if (pendingAction.action === 'add') {
          setEditDepartmentIds([...editDepartmentIds, pendingAction.value]);
        } else if (pendingAction.action === 'remove') {
          setEditDepartmentIds(
            editDepartmentIds.filter((id) => id !== pendingAction.value),
          );
        }
      }
    }
    setPermissionModalOpen(false);
    setPendingAction(null);
  };

  const handlePermissionCancel = () => {
    setPermissionModalOpen(false);
    setPendingAction(null);
  };

  if (!selectedOrg) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Typography
          sx={{
            color: COLORS.labelText,
            fontFamily: '"Inter", sans-serif',
            fontSize: VALUE_SIZE,
          }}
        >
          No organization selected. Please select an organization to view your
          profile.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
          pt: 0,
          mt: 'clamp(-8vh, calc(5vh - 80px + max(0px, (100vh - 800px) * 0.15)), 6vh)',
          pb: 'clamp(8px, 1vh, 24px)',
          '@media (max-width: 1400px)': {
            minWidth: '1200px',
            overflow: 'visible',
          },
        }}
      >
        {/* Account Created - top right */}
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            minWidth: CONTAINER_MIN_WIDTH,
            mx: CONTAINER_MARGIN_X,
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 'clamp(4px, 0.5vh, 8px)',
            mt: 0, // No top margin
            '@media (max-width: 1400px)': {
              width: '1100px',
              minWidth: '1100px',
            },
          }}
        >
          <Typography
            sx={{
              color: COLORS.sectionHeaderBg,
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(12px, 0.8vw, 14px)',
              textAlign: 'right',
            }}
          >
            Account Created
            <br />
            01-10-2025
          </Typography>
        </Box>

        {/* USER PROFILE title - middle left */}
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            minWidth: CONTAINER_MIN_WIDTH,
            mx: CONTAINER_MARGIN_X,
            mb: 'clamp(4px, 0.5vh, 8px)',
            '@media (max-width: 1400px)': {
              width: '1100px',
              minWidth: '1100px',
            },
          }}
        >
          <Typography
            sx={{
              color: COLORS.sectionHeaderBg,
              fontFamily: '"Futura PT", "Futura", sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(28px, 2.5vw, 48px)', // Smaller on laptops
              letterSpacing: 'clamp(5px, 0.57vw, 11px)',
              pl: 'clamp(24px, 4vw, 64px)',
              textShadow:
                'clamp(-3px, -0.26vw, -5px) clamp(2.5px, 0.21vw, 4px) 0px rgba(101, 166, 178, 1)',
            }}
          >
            USER PROFILE
          </Typography>
        </Box>

        {/* Edit Profile button - positioned right above main box */}
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            minWidth: CONTAINER_MIN_WIDTH,
            mx: CONTAINER_MARGIN_X,
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 'clamp(8px, 1vh, 12px)',
            visibility: isEditing ? 'hidden' : 'visible',
            '@media (max-width: 1400px)': {
              width: '1100px',
              minWidth: '1100px',
            },
          }}
        >
          <Button
            onClick={handleStartEdit}
            disableRipple
            sx={{
              backgroundColor: '#0E463F !important',
              color: '#FFFFFF',
              height: EDIT_BUTTON_HEIGHT,
              width: EDIT_BUTTON_WIDTH,
              borderRadius: 0,
              fontFamily: '"Inter", sans-serif',
              fontSize: EDIT_BUTTON_FONT,
              fontWeight: 600,
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'clamp(6px, 0.5vw, 10px)',
              '&:hover': {
                backgroundColor: '#166d61 !important',
              },
              '&:active': {
                backgroundColor: '#0a3833 !important',
              },
            }}
          >
            <span style={{ color: '#FFFFFF' }}>Edit Profile</span>
            <img
              src={pencilIcon}
              alt=''
              style={{
                width: EDIT_BUTTON_ICON,
                height: EDIT_BUTTON_ICON,
                objectFit: 'contain',
              }}
            />
          </Button>
        </Box>

        {/* Main content container */}
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            minWidth: CONTAINER_MIN_WIDTH,
            mx: CONTAINER_MARGIN_X,
            minHeight: CONTAINER_HEIGHT,
            backgroundColor: COLORS.containerBg,
            borderRadius: '8px',
            padding: CONTAINER_PADDING_Y,
            paddingLeft: CONTAINER_PADDING_X,
            paddingRight: CONTAINER_PADDING_X,
            paddingTop:
              'clamp(24px, calc(3vh + max(0px, (100vh - 800px) * 0.25)), 140px)',
            display: 'flex',
            flexDirection: 'row',
            gap: PROFILE_TO_INFO_GAP,
            overflow: 'visible',
            position: 'relative',
            '@media (max-width: 1400px)': {
              width: '1100px',
              minWidth: '1100px',
            },
          }}
        >
          {/* Left side: Profile section (~200px) */}
          <Box
            sx={{
              width: PROFILE_SECTION_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: 'clamp(4px, 0.5vh, 16px)',
            }}
          >
            {/* Profile icon at top */}
            <Box
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 'clamp(12px, 1.5vh, 32px)',
              }}
            >
              <img
                src={profileIcon}
                alt='Profile'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Box>

          {/* Right side: Two rows */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(20px, calc(3vh + max(0px, (100vh - 800px) * 0.2)), 180px)', // Grows with viewport height
            }}
          >
            {/* Top row: Personal Information + Contact Information */}
            <Box
              sx={{
                display: 'flex',
                gap: SECTION_GAP,
                minWidth: 'fit-content',
              }}
            >
              {/* Personal Information section */}
              <Box
                sx={{
                  width: SECTION_HEADER_WIDTH,
                  minWidth: SECTION_HEADER_WIDTH,
                  flexShrink: 0,
                }}
              >
                <SectionHeader title='Personal Information' />
                <Box
                  sx={{
                    backgroundColor: 'transparent',
                    width: SECTION_HEADER_WIDTH,
                    minWidth: SECTION_HEADER_WIDTH,
                    flexShrink: 0,
                    p: 'clamp(12px, 1.5vw, 20px)',
                    display: 'grid',
                    gridTemplateColumns: isEditing
                      ? '3fr 1fr 1fr'
                      : '1.5fr 1fr',
                    gap: isEditing ? 'clamp(80px, 9.5vw, 145px)' : FIELD_GAP,
                  }}
                >
                  {isEditing ? (
                    <>
                      {/* Left column: First Name and Last Name stacked */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'clamp(16px, 2.5vh, 48px)',
                        }}
                      >
                        <FieldEdit
                          label='First Name'
                          value={editFirstName}
                          onChange={setEditFirstName}
                          placeholder='Jane'
                        />
                        <FieldEdit
                          label='Last Name'
                          value={editLastName}
                          onChange={setEditLastName}
                          placeholder='Doe'
                        />
                      </Box>
                      {/* Middle column: Pronouns (will align with Role in Employment section) */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <FieldEdit
                          label='Pronouns'
                          value={editPronouns}
                          onChange={setEditPronouns}
                          placeholder='She/Her'
                        />
                      </Box>
                      {/* Third column: Empty for alignment */}
                      <Box />
                    </>
                  ) : (
                    <>
                      {/* Left column: Full Name */}
                      <Box>
                        <FieldDisplay
                          label='Full Name'
                          value={
                            member
                              ? `${member.userDetails.firstName} ${member.userDetails.lastName}`
                              : undefined
                          }
                        />
                      </Box>
                      {/* Right column: Pronouns (left-aligned, above Role) */}
                      <Box>
                        <FieldDisplay
                          label='Pronouns'
                          value={member?.userDetails.pronouns}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* Contact Information section */}
              <Box
                sx={{
                  width: SECTION_HEADER_WIDTH,
                  minWidth: SECTION_HEADER_WIDTH,
                  flexShrink: 0,
                }}
              >
                <SectionHeader title='Contact Information' />
                <Box
                  sx={{
                    backgroundColor: 'transparent',
                    width: SECTION_HEADER_WIDTH,
                    minWidth: SECTION_HEADER_WIDTH,
                    flexShrink: 0,
                    p: 'clamp(12px, 1.5vw, 20px)',
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 1fr',
                    gap: isEditing ? 'clamp(40px, 5vw, 80px)' : FIELD_GAP,
                  }}
                >
                  {isEditing ? (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'clamp(12px, 1.7vh, 20px)',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          sx={{
                            color: COLORS.labelText,
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize: LABEL_SIZE,
                          }}
                        >
                          Phone Number
                        </Typography>
                        <TextField
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          placeholder='123-456-7890'
                          sx={{
                            ...inputStyles,
                            width: 'clamp(140px, 13vw, 250px)',
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'clamp(12px, 1.7vh, 20px)',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          sx={{
                            color: COLORS.labelText,
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize: LABEL_SIZE,
                          }}
                        >
                          Email
                        </Typography>
                        <Typography
                          sx={{
                            color: '#000000',
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 650,
                            fontSize: VALUE_SIZE,
                            marginTop: 'clamp(2px, 0.3vh, 4px)',
                          }}
                        >
                          {member?.userDetails.email || '-'}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <FieldDisplay
                        label='Phone Number'
                        value={member?.userDetails.phone}
                      />
                      <FieldDisplay
                        label='Email'
                        value={member?.userDetails.email}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Bottom row: Employment Overview section */}
            <Box
              sx={{
                display: 'flex',
                minWidth: 'fit-content',
              }}
            >
              <Box
                sx={{
                  width: SECTION_HEADER_WIDTH,
                  minWidth: SECTION_HEADER_WIDTH,
                  flexShrink: 0,
                }}
              >
                <SectionHeader
                  title='Employment Overview'
                  width={SECTION_HEADER_WIDTH}
                />
                {isEditing ? (
                  <Box
                    sx={{
                      backgroundColor: 'transparent',
                      width: SECTION_HEADER_WIDTH,
                      minWidth: SECTION_HEADER_WIDTH,
                      flexShrink: 0,
                      p: 'clamp(12px, 1.5vw, 20px)',
                      display: 'grid',
                      gridTemplateColumns: '2fr 2fr',
                      gap: 'clamp(110px, 11.5vw, 185px)',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: COLORS.labelText,
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 500,
                          fontSize: LABEL_SIZE,
                          mb: 'clamp(18px, 2.5vh, 29px)',
                        }}
                      >
                        Organization
                      </Typography>
                      <Typography
                        sx={{
                          color: '#000000',
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 650,
                          fontSize: VALUE_SIZE,
                        }}
                      >
                        {selectedOrg?.name || '-'}
                      </Typography>
                    </Box>

                    {/* Nested container for Role and Department */}
                    <Box
                      sx={{ display: 'flex', gap: 'clamp(22px, 3.5vw, 52px)' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          minHeight: 'clamp(120px, 15vh, 160px)',
                        }}
                      >
                        <Typography
                          sx={{
                            color: COLORS.labelText,
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize: LABEL_SIZE,
                            mb: 'clamp(12px, 1.7vh, 20px)',
                          }}
                        >
                          Role
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'clamp(4px, 0.5vh, 8px)',
                          }}
                        >
                          {/* Input box containing selected role tag */}
                          <Box
                            sx={{
                              backgroundColor: 'rgba(14, 70, 63, 0.47)',
                              borderRadius: 0,
                              width: TAG_BUTTON_WIDTH,
                              minHeight: TAG_INPUT_HEIGHT,
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 'clamp(4px, 0.5vh, 8px)',
                              px: 'clamp(6px, 0.6vw, 10px)',
                              py: 'clamp(4px, 0.5vh, 8px)',
                            }}
                          >
                            {/* Selected role tag inside input box */}
                            {editRole && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  backgroundColor: COLORS.tagBg,
                                  color: COLORS.tagText,
                                  height: TAG_HEIGHT,
                                  px: 'clamp(8px, 0.8vw, 12px)',
                                  borderRadius: '4px',
                                  fontFamily: '"Inter", sans-serif',
                                  fontSize: TAG_FONT,
                                  fontWeight: 600,
                                  minWidth: '80px',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                }}
                              >
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                >
                                  {editRole}
                                </span>
                                <span
                                  onClick={handleRemoveRole}
                                  style={{
                                    cursor: 'pointer',
                                    marginLeft: '8px',
                                    fontSize: '14px',
                                    flexShrink: 0,
                                  }}
                                >
                                  x
                                </span>
                              </Box>
                            )}
                          </Box>
                          {/* Add Role button */}
                          <AddTagButton
                            label='Add Role'
                            options={
                              editRole
                                ? roleOptions.filter((r) => r !== editRole)
                                : roleOptions
                            }
                            onSelect={handleSelectRole}
                            showDropdown={showRoleDropdown}
                            onToggleDropdown={() =>
                              setShowRoleDropdown(!showRoleDropdown)
                            }
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          minHeight: 'clamp(120px, 15vh, 160px)',
                        }}
                      >
                        <Typography
                          sx={{
                            color: COLORS.labelText,
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize: LABEL_SIZE,
                            mb: 'clamp(12px, 1.7vh, 20px)',
                          }}
                        >
                          Department
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'clamp(4px, 0.5vh, 8px)',
                          }}
                        >
                          {/* Input box containing selected department tags */}
                          <Box
                            sx={{
                              backgroundColor: 'rgba(14, 70, 63, 0.47)',
                              borderRadius: 0,
                              width: TAG_BUTTON_WIDTH,
                              minHeight: TAG_INPUT_HEIGHT,
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 'clamp(4px, 0.5vh, 8px)',
                              px: 'clamp(6px, 0.6vw, 10px)',
                              py: 'clamp(4px, 0.5vh, 8px)',
                            }}
                          >
                            {/* Selected department tags inside input box */}
                            {editDepartmentIds.map((deptId) => {
                              const dept = availableDepartments.find(
                                (d) => d.id === deptId,
                              );
                              return dept ? (
                                <Box
                                  key={deptId}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: COLORS.tagBg,
                                    color: COLORS.tagText,
                                    height: TAG_HEIGHT,
                                    px: 'clamp(8px, 0.8vw, 12px)',
                                    borderRadius: '4px',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: TAG_FONT,
                                    fontWeight: 600,
                                    minWidth: '80px',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                    }}
                                  >
                                    {dept.name}
                                  </span>
                                  <span
                                    onClick={() =>
                                      handleRemoveDepartment(deptId)
                                    }
                                    style={{
                                      cursor: 'pointer',
                                      marginLeft: '8px',
                                      fontSize: '14px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    x
                                  </span>
                                </Box>
                              ) : null;
                            })}
                          </Box>
                          {/* Add Department button */}
                          <AddTagButton
                            label='Add Department'
                            options={availableDepartments
                              .filter((d) => !editDepartmentIds.includes(d.id))
                              .map((d) => d.name)}
                            onSelect={(deptName: string) => {
                              const dept = availableDepartments.find(
                                (d) => d.name === deptName,
                              );
                              if (dept) handleSelectDepartment(dept.id);
                            }}
                            showDropdown={showDepartmentDropdown}
                            onToggleDropdown={() =>
                              setShowDepartmentDropdown(!showDepartmentDropdown)
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      backgroundColor: 'transparent',
                      width: SECTION_HEADER_WIDTH,
                      minWidth: SECTION_HEADER_WIDTH,
                      flexShrink: 0,
                      p: 'clamp(12px, 1.5vw, 20px)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'clamp(24px, 3vh, 48px)',
                    }}
                  >
                    {/* Top row: Organization and Role */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.5fr 1fr',
                        gap: FIELD_GAP,
                      }}
                    >
                      <FieldDisplay
                        label='Organization'
                        value={selectedOrg?.name}
                      />
                      <FieldDisplay
                        label='Role'
                        value={member?.role || 'General User'}
                      />
                    </Box>
                    {/* Bottom row: Department */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.5fr 1fr',
                        gap: SECTION_GAP,
                      }}
                    >
                      <FieldDisplay
                        label='Department'
                        value={member?.userDetails.departments
                          ?.map((d) => d.name)
                          .join(', ')}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Save and Quit button in edit mode */}
            {isEditing && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 'clamp(12px, 1.5vw, 20px)',
                  mt: 'auto',
                  flexWrap: 'nowrap',
                }}
              >
                <Button
                  onClick={handleCancelEdit}
                  disableRipple
                  sx={{
                    backgroundColor: '#FFFFFF !important',
                    color: 'rgba(14, 70, 63, 1) !important',
                    height: NAV_BUTTON_HEIGHT,
                    width: SAVE_BUTTON_WIDTH,
                    borderRadius: 0,
                    fontFamily: '"Inter", sans-serif',
                    fontSize: NAV_BUTTON_FONT,
                    fontWeight: 600,
                    textTransform: 'none',
                    transform:
                      'translate(calc(-2vw - 20px), calc(-3vh - 10px))',
                    '&:hover': {
                      backgroundColor: '#f0f0f0 !important',
                      color: 'rgba(14, 70, 63, 1) !important',
                    },
                    '&:active': {
                      backgroundColor: '#e0e0e0 !important',
                      color: 'rgba(14, 70, 63, 1) !important',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  disableRipple
                  sx={{
                    backgroundColor: '#0E463F !important',
                    color: '#FFFFFF',
                    height: NAV_BUTTON_HEIGHT,
                    width: SAVE_BUTTON_WIDTH,
                    borderRadius: 0,
                    fontFamily: '"Inter", sans-serif',
                    fontSize: NAV_BUTTON_FONT,
                    fontWeight: 500,
                    textTransform: 'none',
                    transform:
                      'translate(calc(-2vw - 20px), calc(-3vh - 10px))',
                    '&:hover': {
                      backgroundColor: '#166d61 !important',
                    },
                    '&:active': {
                      backgroundColor: '#0a3833 !important',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#4a7d78 !important',
                      color: 'rgba(255, 255, 255, 0.4)',
                    },
                  }}
                >
                  <span style={{ color: '#FFFFFF' }}>
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </span>
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Permission Modal */}
        <PermissionModal
          open={permissionModalOpen}
          onClose={handlePermissionCancel}
          onConfirm={handlePermissionConfirm}
          message={permissionModalMessage}
        />
      </Box>
    </>
  );
}
